import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ButtonLink } from '@backstage/ui';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';
import { createPortal } from 'react-dom';
import { buildWriterUrl } from './TechDocsEditButton.utils';
import {
  ensureTechDocsShadowStyles,
  getCurrentThemeMode,
} from '../techDocsShadowStyles';

const EDIT_LINK_SELECTOR = '[title^="Edit this page"]';

type TechDocsEditButtonContentProps = {
  editLinkOverride?: HTMLAnchorElement | null;
  containerOverride?: HTMLElement | null;
};

export const TechDocsEditButtonContent = ({
  editLinkOverride,
  containerOverride,
}: TechDocsEditButtonContentProps) => {
  const { pathname } = useLocation();
  const [editLink] = useShadowRootElements<HTMLAnchorElement>([
    EDIT_LINK_SELECTOR,
  ]);
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(
    containerOverride ?? null,
  );
  const [themeMode, setThemeMode] = useState(getCurrentThemeMode);
  const resolvedEditLink = editLinkOverride ?? editLink;

  const writerUrl = useMemo(
    () =>
      buildWriterUrl({
        pathname,
      }),
    [pathname],
  );

  useEffect(() => {
    if (containerOverride) {
      setContainerEl(containerOverride);
      return;
    }
    if (!resolvedEditLink) return;

    const computed = window.getComputedStyle(resolvedEditLink);
    resolvedEditLink.style.display = 'none';

    const parent = resolvedEditLink.parentElement;
    if (!parent) return;

    let container = parent.querySelector(
      '[data-techdocs-edit-button]',
    ) as HTMLElement | null;

    if (!container) {
      container = document.createElement('span');
      container.dataset.techdocsEditButton = 'true';
      container.style.cssFloat =
        computed.cssFloat === 'none' ? 'right' : computed.cssFloat;
      container.style.marginLeft =
        computed.marginLeft === '0px' ? '0.4rem' : computed.marginLeft;
      container.style.marginRight = computed.marginRight;
      container.style.display = 'inline-flex';
      container.style.alignItems = 'center';
      parent.insertBefore(container, resolvedEditLink.nextSibling);
    }

    setContainerEl(container);
  }, [containerOverride, resolvedEditLink]);

  useEffect(() => {
    if (!containerEl) return;
    const root = containerEl.getRootNode();
    if (root instanceof ShadowRoot) {
      ensureTechDocsShadowStyles(root);
    }
  }, [containerEl]);

  useEffect(() => {
    const updateThemeMode = () => setThemeMode(getCurrentThemeMode());
    const observer = new MutationObserver(updateThemeMode);
    observer.observe(document.body, {
      attributeFilter: ['data-theme-mode', 'class'],
      attributes: true,
      childList: true,
      subtree: true,
    });
    updateThemeMode();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerEl) return;

    containerEl.classList.add('traton');
    containerEl.dataset.techdocsBuiRoot = 'true';
    containerEl.classList.toggle('tl-mode-light', themeMode === 'light');
    containerEl.classList.toggle('tl-mode-dark', themeMode === 'dark');
    containerEl.dataset.themeMode = themeMode;
  }, [containerEl, themeMode]);

  if (!containerEl) {
    return null;
  }

  const content = (
    <ButtonLink
      variant="tertiary"
      href={writerUrl}
      iconEnd={<span className="tl-icon tl-icon--edit" aria-hidden="true" />}
    >
      Edit
    </ButtonLink>
  );

  return createPortal(content, containerEl);
};

const techDocsEditButtonPlugin = createPlugin({
  id: 'techDocsEditButton',
});

export const TechDocsEditButton = techDocsEditButtonPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsEditButton',
    location: TechDocsAddonLocations.Content,
    component: TechDocsEditButtonContent,
  }),
);
